import { Localized } from './localizedStrings';

/**
 * Gets the user's current browser LCID
 * @returns {number} The LCID (e.g., 1033 for en-US, 1036 for fr-FR)
 */
export function getUserLcid() {
    const browserLang = Localized.lang || 'en';
    const lcidString = Localized.get(`lcid-${browserLang}`);
    return lcidString ? parseInt(lcidString, 10) : 1033; // Default to English (1033)
}

/**
 * Extracts the localized value from a multi-lingual array based on user's locale
 * Multi-lingual format: [{ lcid: 1033, value: "text" }, { lcid: 1036, value: "texte" }]
 *
 * @param {Array} multiLingualArray - Array of objects with lcid and value properties
 * @param {number} userLcid - Optional LCID to use (defaults to browser LCID)
 * @returns {string} The localized value or empty string if not found
 */
export function getLocalizedValue(multiLingualArray, userLcid = null) {
    if (!Array.isArray(multiLingualArray) || multiLingualArray.length === 0) {
        return '';
    }

    const targetLcid = userLcid || getUserLcid();

    // Try to find matching LCID
    const matchingEntry = multiLingualArray.find(entry => entry.lcid === targetLcid);
    if (matchingEntry) {
        return matchingEntry.value || '';
    }

    // Fallback to first value
    return multiLingualArray[0]?.value || '';
}

/**
 * Extracts the localized value from a StringMultiLingual object
 * This handles both direct array format and defaultValue format
 *
 * @param {Object|Array} multiLingualValue - Either array format or object with defaultValue
 * @param {number} userLcid - Optional LCID to use (defaults to browser LCID)
 * @returns {string} The localized value or empty string
 */
export function getMultiLingualString(multiLingualValue, userLcid = null) {
    if (!multiLingualValue) {
        return '';
    }

    // Handle array format: [{ lcid: 1033, value: "text" }]
    if (Array.isArray(multiLingualValue)) {
        return getLocalizedValue(multiLingualValue, userLcid);
    }

    // Handle object with defaultValue (backward compatibility)
    if (multiLingualValue.defaultValue) {
        return multiLingualValue.defaultValue;
    }

    // Handle object with values array
    if (Array.isArray(multiLingualValue.values)) {
        return getLocalizedValue(multiLingualValue.values, userLcid);
    }

    return '';
}
